// Push notification utilities for Punjab Imports Staff App

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Get the current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none'
    });
    console.log('Service Worker registered:', registration.scope);
    
    // Trigger an immediate update check so devices pick up the new push
    // handler (sw.js v5+) as soon as they reload.
    try { await registration.update(); } catch (_) {}
    
    // Check for updates periodically
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000); // Check every hour
    
    // Handle updates — auto-apply silently so the push handler is never stale.
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('Service Worker update found');
      
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('New Service Worker version ready — activating silently');
          newWorker.postMessage({ type: 'SKIP_WAITING' });
          // Do NOT auto-reload — disruptive. The new SW takes over on next page navigation.
        }
      });
    });
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
};

// Get VAPID public key from server
const getVapidPublicKey = async (token) => {
  const response = await fetch(`${API_URL}/api/push/vapid-public-key`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to get VAPID public key');
  }
  
  const data = await response.json();
  return data.publicKey;
};

// Convert base64 URL to Uint8Array (required for push subscription)
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Convert ArrayBuffer → base64url (no padding, URL-safe alphabet).
// CRITICAL for Safari / APNs: RFC 8291 mandates base64url for the p256dh
// and auth keys. Chrome/FCM tolerate standard base64, but Safari silently
// drops pushes when the encoding is wrong — this is the #1 reason "Safari
// never gets notifications" even though subscribe appears to succeed.
const arrayBufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Check if the current browser is Safari on iOS (which requires PWA install
// for web push to work at all). Used for diagnostic messaging.
export const isIOSSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && /^((?!CriOS|FxiOS|EdgiOS).)*Safari/.test(ua);
};

export const isInStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
};

// Subscribe to push notifications
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) {
    // iOS Safari without PWA install has no PushManager at all.
    if (isIOSSafari() && !isInStandalonePWA()) {
      throw new Error(
        'On iPhone/iPad, you must first tap Share → Add to Home Screen, then open PIE from the Home Screen icon before enabling notifications.'
      );
    }
    throw new Error('Push notifications not supported in this browser');
  }
  
  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Get VAPID public key
  const vapidPublicKey = await getVapidPublicKey(token);
  
  // Before subscribing, verify we don't already have a subscription bound to
  // a DIFFERENT VAPID key. If we do, pushManager.subscribe() will throw an
  // InvalidStateError on Safari. Unsubscribe first so the new sub is clean.
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
  } catch (_) { /* ignore */ }

  // Subscribe to push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  
  // Send subscription to server — keys MUST be base64url (not standard base64).
  const response = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64Url(subscription.getKey('auth'))
      }
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to save subscription on server');
  }
  
  return subscription;
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (token) => {
  if (!isPushSupported()) {
    return;
  }
  
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    // Unsubscribe locally
    await subscription.unsubscribe();
    
    // Remove from server
    await fetch(`${API_URL}/api/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64Url(subscription.getKey('auth'))
        }
      })
    });
  }
};

// Check if already subscribed to push
export const isPushSubscribed = async () => {
  if (!isPushSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
};

// Get push subscription status from server
export const getPushStatus = async (token) => {
  const response = await fetch(`${API_URL}/api/push/status`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    return { subscribed: false, subscription_count: 0 };
  }
  
  return response.json();
};
